import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, TrendingUp, Activity, Clock, Trophy, AlertCircle } from 'lucide-react';
import NFLService from '../../../components/dashboards/services/nflService';

const NFLOddsModal = ({ game, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [odds, setOdds] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('spread');
  const [oddsFormat, setOddsFormat] = useState('american'); // 'american' or 'decimal'

  // Fetch odds when modal opens
  useEffect(() => {
    if (!game?.game?.id) return;

    const fetchOdds = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch odds from Bet365 (bookmaker ID: 4)
        const response = await NFLService.getOdds(game.game.id, 4);
        setOdds(response.response?.[0] || null);
      } catch (err) {
        console.error('Failed to fetch odds:', err);
        setError('Failed to load odds. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOdds();
  }, [game]);

  // Convert decimal odds to American odds
  const decimalToAmerican = (decimal) => {
    if (!decimal || decimal === 'N/A') return 'N/A';
    const dec = parseFloat(decimal);
    if (isNaN(dec)) return decimal;

    if (dec >= 2.0) {
      // Underdog: positive odds
      return '+' + Math.round((dec - 1) * 100);
    } else {
      // Favorite: negative odds
      return Math.round(-100 / (dec - 1));
    }
  };

  // Format odds based on selected format
  const formatOdds = (decimal) => {
    if (oddsFormat === 'american') {
      return decimalToAmerican(decimal);
    }
    return decimal; // Return decimal as-is
  };

  const isLive = game?.game?.status?.short && ['Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT'].includes(game.game.status.short);
  const homeTeam = game?.teams?.home;
  const awayTeam = game?.teams?.away;

  const tabs = [
    { id: 'spread', label: 'Spread', icon: TrendingUp },
    { id: 'moneyline', label: 'Money Line', icon: DollarSign },
    { id: 'totals', label: 'Totals', icon: Activity },
    ...(isLive ? [{ id: 'live', label: 'Live', icon: Clock }] : [])
  ];

  // Memoize the expensive odds parsing to prevent recalculation on every render
  const parsedOdds = useMemo(() => {
    if (!odds || !odds.bookmakers?.length) return null;

    // Parse actual Bet365 odds from API response
    const bookmaker = odds.bookmakers[0]; // Should be Bet365 (ID: 4)
    const bets = bookmaker?.bets || [];

    // Helper function to parse bet values
    const parseBet = (betName) => {
      const bet = bets.find(b => b.name === betName);
      return bet?.values || [];
    };

    // Get Asian Handicap spreads (this is the point spread in API-Sports)
    const spreadValues = parseBet('Asian Handicap');

    // Find the main spread line
    // Strategy: Find the spread where BOTH "Home -X" and "Away -X" have odds closest to even (1.90-1.91)
    // This is the main betting line
    const findMainSpread = (values) => {
      if (!values || values.length === 0) return { home: null, away: null };

      // Group by absolute spread value and sign
      const spreadsByValue = {};

      values.forEach(v => {
        const match = v.value?.match(/(Home|Away)\s*([+-])(\d+\.?\d*)/);
        if (!match) return;

        const team = match[1].toLowerCase();
        const sign = match[2];
        const number = match[3];
        const key = `${sign}${number}`;

        if (!spreadsByValue[key]) {
          spreadsByValue[key] = {};
        }

        spreadsByValue[key][team] = {
          value: `${sign}${number}`,
          odd: v.odd
        };
      });

      // Find the negative spread with the most balanced odds and largest spread value
      // Strategy: Prioritize larger spreads with both odds in reasonable range (1.40-3.00)
      let bestSpread = null;
      let largestBalancedSpread = -1;

      Object.entries(spreadsByValue).forEach(([spreadKey, teams]) => {
        if (!spreadKey.startsWith('-')) return; // Only look at negative spreads
        if (!teams.home || !teams.away) return;

        const homeOdd = parseFloat(teams.home.odd);
        const awayOdd = parseFloat(teams.away.odd);
        const spreadValue = parseFloat(spreadKey.substring(1));

        // Check if both odds are in a reasonable betting range
        const bothReasonable = homeOdd >= 1.40 && homeOdd <= 3.00 && awayOdd >= 1.40 && awayOdd <= 3.00;

        // Pick the largest spread where both odds are reasonable
        if (bothReasonable && spreadValue > largestBalancedSpread) {
          largestBalancedSpread = spreadValue;
          const spreadNum = spreadKey.substring(1); // Remove the minus sign

          // Determine which team is favorite (lower odds on negative spread)
          // Lower odds = favorite = gets the negative spread
          if (homeOdd < awayOdd) {
            // Home is favorite (lower odds), so home gets negative spread
            // But we need to use the corresponding + spread odds for the away team
            const plusKey = `+${spreadValue}`;
            const awayPlusOdd = spreadsByValue[plusKey]?.away?.odd || teams.away.odd;

            bestSpread = {
              home: { value: `-${spreadValue}`, odd: teams.home.odd },
              away: { value: `+${spreadValue}`, odd: awayPlusOdd }
            };
          } else {
            // Away is favorite (lower odds), so away gets negative spread
            const plusKey = `+${spreadValue}`;
            const homePlusOdd = spreadsByValue[plusKey]?.home?.odd || teams.home.odd;

            bestSpread = {
              home: { value: `+${spreadValue}`, odd: homePlusOdd },
              away: { value: `-${spreadValue}`, odd: teams.away.odd }
            };
          }
        }
      });

      return bestSpread || { home: null, away: null };
    };

    const mainSpread = findMainSpread(spreadValues);

    // Parse moneyline (Home/Away)
    const moneylineValues = parseBet('Home/Away');
    const moneylineHome = moneylineValues.find(v => v.value === 'Home');
    const moneylineAway = moneylineValues.find(v => v.value === 'Away');

    // Parse totals (Over/Under)
    const totalsValues = parseBet('Over/Under');
    const overValue = totalsValues.find(v => v.value?.includes('Over'));
    const underValue = totalsValues.find(v => v.value?.includes('Under'));

    return {
      spread: {
        home: {
          line: mainSpread.home?.value || 'N/A',
          odds: formatOdds(mainSpread.home?.odd)
        },
        away: {
          line: mainSpread.away?.value || 'N/A',
          odds: formatOdds(mainSpread.away?.odd)
        }
      },
      moneyline: {
        home: { odds: formatOdds(moneylineHome?.odd) },
        away: { odds: formatOdds(moneylineAway?.odd) }
      },
      totals: {
        over: {
          line: overValue?.value?.replace('Over ', '') || 'N/A',
          odds: formatOdds(overValue?.odd)
        },
        under: {
          line: underValue?.value?.replace('Under ', '') || 'N/A',
          odds: formatOdds(underValue?.odd)
        }
      }
    };
  }, [odds, oddsFormat]); // Re-calculate when odds data or format changes

  if (!game) return null;

  const renderOddsContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto mb-4 text-red-400" size={48} />
          <p className="text-white/60">{error}</p>
        </div>
      );
    }

    if (!parsedOdds) {
      return (
        <div className="text-center py-8">
          <Trophy className="mx-auto mb-4 text-white/40" size={48} />
          <p className="text-white/60">No odds available for this game</p>
          <p className="text-white/40 text-sm mt-2">
            Odds are typically available 1-7 days before the game
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {activeTab === 'spread' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Point Spread</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {awayTeam?.logo && (
                    <img src={awayTeam.logo} alt={awayTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{awayTeam?.name}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-blue-400">{parsedOdds.spread.away.line}</span>
                  <span className="text-sm text-white/60">{parsedOdds.spread.away.odds}</span>
                </div>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {homeTeam?.logo && (
                    <img src={homeTeam.logo} alt={homeTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{homeTeam?.name}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-blue-400">{parsedOdds.spread.home.line}</span>
                  <span className="text-sm text-white/60">{parsedOdds.spread.home.odds}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'moneyline' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Money Line</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {awayTeam?.logo && (
                    <img src={awayTeam.logo} alt={awayTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{awayTeam?.name}</p>
                </div>
                <span className="text-xl font-bold text-blue-400">{parsedOdds.moneyline.away.odds}</span>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {homeTeam?.logo && (
                    <img src={homeTeam.logo} alt={homeTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{homeTeam?.name}</p>
                </div>
                <span className="text-xl font-bold text-blue-400">{parsedOdds.moneyline.home.odds}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'totals' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Total Points</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <p className="text-sm font-medium text-white mb-2">Over</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-blue-400">{parsedOdds.totals.over.line}</span>
                  <span className="text-sm text-white/60">{parsedOdds.totals.over.odds}</span>
                </div>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-blue-400/50 transition-colors">
                <p className="text-sm font-medium text-white mb-2">Under</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-blue-400">{parsedOdds.totals.under.line}</span>
                  <span className="text-sm text-white/60">{parsedOdds.totals.under.odds}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'live' && isLive && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-white/80">Live Betting Markets</span>
            </div>
            <div className="text-center py-8">
              <p className="text-white/60">Live odds updating...</p>
              <p className="text-white/40 text-sm mt-2">Check back for real-time betting opportunities</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Betting Odds</h2>
              <div className="flex items-center gap-3">
                {/* Odds Format Toggle */}
                <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setOddsFormat('american')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      oddsFormat === 'american'
                        ? 'bg-white text-blue-600'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    American
                  </button>
                  <button
                    onClick={() => setOddsFormat('decimal')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      oddsFormat === 'decimal'
                        ? 'bg-white text-blue-600'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    Decimal
                  </button>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            {/* Game Info */}
            <div className="flex items-center justify-center gap-4 text-white">
              <div className="text-center">
                {awayTeam?.logo && (
                  <img src={awayTeam.logo} alt={awayTeam.name} className="w-12 h-12 object-contain mx-auto mb-2" />
                )}
                <p className="font-medium">{awayTeam?.name}</p>
                {(game.scores?.away?.total !== undefined) && (
                  <p className="text-2xl font-bold mt-1">{game.scores.away.total}</p>
                )}
              </div>
              <div className="text-xl font-bold">@</div>
              <div className="text-center">
                {homeTeam?.logo && (
                  <img src={homeTeam.logo} alt={homeTeam.name} className="w-12 h-12 object-contain mx-auto mb-2" />
                )}
                <p className="font-medium">{homeTeam?.name}</p>
                {(game.scores?.home?.total !== undefined) && (
                  <p className="text-2xl font-bold mt-1">{game.scores.home.total}</p>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-500/20 border-b-2 border-blue-400 text-blue-400'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-6 max-h-[400px] overflow-y-auto">
            {renderOddsContent()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NFLOddsModal;